import React, { useState } from 'react';
import { requestsAPI } from '../services/api';

const RequestStatusUpdater = ({ 
  request, 
  onUpdate, 
  isAdmin = false, 
  customerId = null 
}) => {
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState(null);
  
  // Define available status transitions based on current status and user role
  const getAvailableStatuses = () => {
    const currentStatus = request.status;
    
    // Admin can update to more status options
    if (isAdmin) {
      switch (currentStatus) {
        case 'draft':
          return ['pending', 'rejected'];
        case 'pending':
          return ['approved', 'rejected'];
        case 'approved':
          return ['in_progress', 'cancelled'];
        case 'in_progress':
          return (request.requestType || request.request_type) === 'ship'
            ? ['cancelled']
            : ['completed', 'cancelled'];
        default:
          return [];
      }
    }
    
    // Customer can only cancel or submit pending requests
    switch (currentStatus) {
      case 'draft':
        return ['pending', 'cancelled'];
      case 'pending':
        return ['cancelled'];
      case 'approved':
        return ['cancelled'];
      default:
        return [];
    }
  };
  
  // Get name for display
  const getStatusName = (status) => {
    const statusMap = {
      'draft': 'Draft',
      'pending': 'Pending Approval',
      'approved': 'Approved',
      'in_progress': 'In Progress',
      'completed': 'Completed',
      'cancelled': 'Cancelled',
      'rejected': 'Rejected'
    };
    
    return statusMap[status] || status;
  };
  
  // Handle status update
  const handleStatusUpdate = async (newStatus) => {
    try {
      setUpdating(true);
      setError(null);
      
      // Special case for cancellation
      if (newStatus === 'cancelled') {
        await requestsAPI.cancelRequest(request.id);
      } else {
        await requestsAPI.updateRequest(request.id, {
          status: newStatus
        });
      }
      
      // If update successful, call the onUpdate callback
      if (onUpdate) {
        onUpdate(newStatus);
      }
    } catch (err) {
      console.error('Failed to update request status:', err);
      setError(err.response?.data?.message || 'Failed to update request status');
    } finally {
      setUpdating(false);
    }
  };
  
  // Get status badge class
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'approved':
        return 'bg-blue-100 text-blue-800';
      case 'in_progress':
        return 'bg-indigo-100 text-indigo-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  
  const availableStatuses = getAvailableStatuses();
  
  return (
    <div className="space-y-4">
      <div className="flex items-center space-x-2">
        <span className="text-sm font-medium text-gray-500">Current Status:</span>
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClass(request.status)}`}>
          {getStatusName(request.status)}
        </span>
      </div>
      
      {availableStatuses.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-medium text-gray-700">
            {isAdmin ? 'Update Status:' : 'Available Actions:'}
          </p>
          
          <div className="flex flex-wrap gap-2">
            {availableStatuses.map((status) => (
              <button
                key={status}
                type="button"
                onClick={() => handleStatusUpdate(status)}
                disabled={updating}
                className={`inline-flex items-center px-3 py-1.5 border text-sm font-medium rounded-md shadow-sm
                  ${status === 'cancelled' || status === 'rejected' 
                    ? 'border-red-300 text-red-700 bg-white hover:bg-red-50' 
                    : 'border-indigo-300 text-indigo-700 bg-white hover:bg-indigo-50'
                  }
                  ${updating ? 'opacity-50 cursor-not-allowed' : ''}
                `}
              >
                {status === 'pending' && request.status === 'draft' ? 'Submit for Approval' : ''}
                {status === 'approved' ? 'Approve Request' : ''}
                {status === 'in_progress' ? 'Mark In Progress' : ''}
                {status === 'completed' ? 'Mark as Completed' : ''}
                {status === 'cancelled' ? 'Cancel Request' : ''}
                {status === 'rejected' ? 'Reject Request' : ''}
              </button>
            ))}
          </div>
          
          {error && (
            <p className="mt-1 text-sm text-red-600">
              {error}
            </p>
          )}
        </div>
      )}
      
      {/* Admin-specific approval information */}
      {isAdmin && request.status === 'pending' && request.requestType === 'incoming' && (
        <div className="mt-4 p-4 bg-yellow-50 rounded-md border border-yellow-200">
          <h4 className="text-sm font-medium text-yellow-800">Incoming Inventory Approval Information</h4>
          <p className="text-sm text-yellow-700 mt-1">
            Before approving, please verify warehouse capacity for:
          </p>
          <ul className="mt-2 text-sm text-yellow-700 list-disc list-inside">
            <li>Space requirements for pallets and cartons</li>
            <li>Scheduling for receiving staff</li>
            <li>Special handling needs</li>
          </ul>
        </div>
      )}
    </div>
  );
};

export default RequestStatusUpdater;
